import { faker } from '@faker-js/faker'

import { ENRL, CLEN, SCLR, ATTN, LeaTestDataModel, defaultLeaData, CoreDataModel, RecordType } from '@/models.ts'
import { CHLD, CHLD_Key } from '@/models/chld.tsx'
import { FAMI } from '@/models/fami.tsx'
import { CLRM } from '@/models/clrm.tsx'
import { STFF, SASS} from '@/models/stff'
import { ISODateStringToYYYYMMDD, makeRecord } from '@/utils/util'

// Used for faker date conversions since all faker datetime values are in UTC
function dateToLocalString(d: Date): string {
  return new Date(d.getTime() + d.getTimezoneOffset() * 60000)
    .toJSON()
    .slice(0, 10);
}

function fakerArrayElement<T>(options: T[]) : T {
    return options[faker.number.int(options.length - 1)]
}

function fakerProgramYear(core: CoreDataModel): ProgramYearModel {
    //const py = fakerArrayElement(core.programYears)
    const py = core.programYears[core.programYears.length - 1]
    const today = dateToLocalString(new Date())
    if (py.EffectiveEndDate > today) {
        py.EffectiveEndDate = today
    }
    return py
}

const newClassroomModel = (core: CoreDataModel, data: LeaTestDataModel, clrm: CLRM | undefined): CLRM => {
    const py = fakerProgramYear(core)
    return {
        Id: faker.number.int(1000000).toString(),
        ReportingLEA: data.lea.CDSCode,
        PreschoolCode: fakerArrayElement(data.preschools).PreschoolCDSCode,
        ProgramYear: py.Code,
        LocalClassroomId: clrm?.LocalClassroomId ?? faker.number.int(1000000).toString(),
        ClassroomName: `${faker.person.fullName()}'s Class`.substring(0, 30),
        ContractTypeOffered1: fakerArrayElement(core.contractTypesOfferedList).Value,
        ProgramTypeOffered1: fakerArrayElement(core.programTypeList).Value,
        ProgramDurationOffered1: fakerArrayElement(core.programDurationList).Value,
        SettingTypeOffered: fakerArrayElement(core.settingTypesOfferedList).Value,
        MHCSOffered: ['Y', 'N'][faker.number.int(1)],
        LanguageUsedInClassroom1: fakerArrayElement(core.classroomLanguages).Value,
        LanguageProgramType: fakerArrayElement(core.languageProgramType).Value,
        VolunteerFrequency: fakerArrayElement(core.volunteerFrequencyList).Value,
        EffectiveStartDate: dateToLocalString(faker.date.between({ from: py.EffectiveStartDate, to: py.EffectiveEndDate })),
        EffectiveEndDate: null,

        StaffAssignments: [],
        ChildAssignments: [],
    }
}

export function newClassrooms(core: CoreDataModel, data: LeaTestDataModel, numRecords: number, percentNew: number = 100): CLRM[] {
    if (data.clrm.length == 0) percentNew = 100
    const classroomPool = Array.from(Array(numRecords).keys(), (idx: number) => {
        return (faker.number.int(99) < percentNew) ? undefined : data.clrm[faker.number.int(data.clrm.length - 1)]
    })
    return Array.from(Array(numRecords).keys(), (idx: number) => { return newClassroomModel(core, data, classroomPool[idx]) });
}

const newStaffModel = (core: CoreDataModel, data: LeaTestDataModel, staff: STFF | undefined): STFF => {
    const py = fakerProgramYear(core)
    const gender = core.genderList[faker.number.int(core.genderList.length - 1)].Value
    const race = core.raceList[faker.number.int(core.raceList.length)]?.Value || ''
    const ethnicity = ['Y', 'N', ''][faker.number.int(2)]
    const waiver = ['Y', 'N'][faker.number.int(1)]
    const effectiveStart = faker.date.between({ from: py.EffectiveStartDate, to: py.EffectiveEndDate })
    const permitExpiration = faker.date.future()
    const curentStart = faker.date.past({ refDate: effectiveStart })
    return {
        Id: faker.number.int(1000000).toString(),
        ReportingLEA: data.lea.CDSCode,
        ProgramYear: py.Code,
        LocalStaffId: staff?.LocalStaffId ?? faker.number.int(1000000).toString(),
        StaffLegalLastName: faker.person.lastName(gender == "F" ? "female" : "male"),
        StaffLegalFirstName: faker.person.firstName(gender == "F" ? "female" : "male"),
        StaffLegalMiddleName: '',
        StaffDateOfBirth: dateToLocalString(faker.date.birthdate({mode: 'age', min: 21, max: 60 })),
        StaffGender: gender,
        StaffRace1: race,
        StaffEthnicity: ethnicity,
        StaffEthnicityMissingIndicator: ethnicity === '' ? 'Y' : 'N',
        StaffRaceMissingIndicator: race === '' ? 'Y' : 'N',
        StaffLanguage1: core.languagesList[1 + faker.number.int(core.languagesList.length - 2)].Value,
        StaffHighestDegreeCode: fakerArrayElement(core.educationList).Value,
        StaffWaiver: waiver,
        StaffWaiverType: fakerArrayElement(core.waiverTypes).Value,
        StaffPermitIssuedDate: (waiver === 'N') ? dateToLocalString(faker.date.past({ years: 2, refDate: effectiveStart })) : '',
        StaffPermitExpirationDate: (waiver === 'N') ? dateToLocalString(permitExpiration) : '',
        StaffWaiverIssuedDate: (waiver === 'Y') ? dateToLocalString(faker.date.past()) : '',
        StaffWaiverExpirationDate: (waiver === 'Y') ? dateToLocalString(faker.date.soon()) : '',
        SEID: (1000000000 + faker.number.int(9000000000 - 1)).toString(),
        StaffPermitOrCredentialNumber: (waiver === 'N') ? faker.number.int(10000000).toString() : '',
        StaffPermitOrCredentialType: fakerArrayElement(core.staffPermitList).Value,
        StaffRoleName: fakerArrayElement(core.staffRoleList).Value,
        StaffCurrentEmploymentStartDate: dateToLocalString(curentStart),
        StaffOverallEmploymentStartDate: dateToLocalString(faker.date.past({ refDate: curentStart })),
        EffectiveStartDate: dateToLocalString(effectiveStart),
        EffectiveEndDate: null,

        ClassroomAssignments: [],
    }
}

export function newStaff(core: CoreDataModel, data: LeaTestDataModel, numRecords: number, percentNew: number = 0): STFF[] {
    if (data.stff.length == 0) percentNew = 100
    const staffPool = Array.from(Array(numRecords).keys(), (idx: number) => {
        return (faker.number.int(99) < percentNew) ? undefined : data.stff[faker.number.int(data.stff.length - 1)]
    })
    return Array.from(Array(numRecords).keys(), (idx: number) => { return newStaffModel(core, data, staffPool[idx]) });
}

const newStaffAssignment = (core: CoreDataModel, data: LeaTestDataModel, staff: STFF): SASS => {
    const pctTime = faker.number.int({ min: 50, max: 200 })
    var r1 = fakerArrayElement(core.staffRoleList).Value
    var r1PctTime = 1 + faker.number.int(pctTime - 1)
    var r2 = fakerArrayElement(core.staffRoleList).Value
    var r2PctTime = faker.number.int(pctTime - r1PctTime)
    if (r1 === r2 || r2PctTime == 0) {
        r2 = ""; r2PctTime = 0;
    }
    var n1 = fakerArrayElement(core.nonClassroomRoleList).Value
    var n1PctTime = faker.number.int(pctTime - r1PctTime - r2PctTime)
    if (n1PctTime == 0) {
        n1 = ""; n1PctTime = 0;
    }
    var n2 = fakerArrayElement(core.nonClassroomRoleList).Value
    var n2PctTime = faker.number.int(pctTime - r1PctTime - r2PctTime - n1PctTime)
    if (n1 === n2 || n1 == "" || n2PctTime == 0) {
        n2 = ""; n2PctTime = 0;
    }
    var n3 = fakerArrayElement(core.nonClassroomRoleList).Value
    var n3PctTime = pctTime - r1PctTime - r2PctTime - n1PctTime - n2PctTime
    if (n1 === n3 || n2 === n3 || n2 === "" || n3PctTime == 0) {
        n3 = ""; n3PctTime = 0;
    }

    return {
        Id: faker.number.int(1000000).toString(),
        ReportingLEA: staff.ReportingLEA,
        PreschoolCode: fakerArrayElement(data.preschools).PreschoolCDSCode,
        ProgramYear: staff.ProgramYear,
        LocalStaffId: staff.LocalStaffId,
        ClassroomRole1: r1,
        ClassroomRole1PercentOfTime: r1PctTime,
        ClassroomRole2: r2,
        ClassroomRole2PercentOfTime: r2PctTime,
        NonClassroomRole1: n1,
        NonClassroomRole1PercentOfTime: n1PctTime,
        NonClassroomRole2: n2,
        NonClassroomRole2PercentOfTime: n2PctTime,
        NonClassroomRole3: n3,
        NonClassroomRole3PercentOfTime: n3PctTime,
    }
}

export function newStaffAssignments(core: CoreDataModel, data: LeaTestDataModel, numRecords: number): SASS[] {
    const staffPool = Array.from(Array(numRecords).keys(), (idx: number) => {
        return data.stff[faker.number.int(data.stff.length - 1)];
    });
    return Array.from(Array(numRecords).keys(), (idx: number) => { return newStaffAssignment(core, data, staffPool[idx]) });
}

const newFamilyModel = (core: CoreDataModel, data: LeaTestDataModel, family: FAMI | undefined): FAMI => {
    const py = fakerProgramYear(core)
    const gender1 = core.genderList[faker.number.int(core.genderList.length - 1)].Value
    const gender2 = core.genderList[faker.number.int(core.genderList.length)]?.Value
    const child = (data.chld.length == 0) ? undefined : data.chld[faker.number.int(data.chld.length - 1)]
    const firstName1 = faker.person.lastName(gender1)
    const lastName1 = faker.person.lastName(gender1)
    return {
        Id: faker.number.int(1000000).toString(),
        ReportingLEA: data.lea.CDSCode,
        PreschoolCode: child?.PreschoolCode ?? data.preschools[faker.number.int(data.preschools.length - 1)].PreschoolCDSCode,
        ProgramYear: py.Code,
        LocalFamilyId: family.LocalFamilyId ?? faker.number.int(1000000).toString(),
        LocalChildId: child?.LocalChildId ?? faker.number.int(1000000).toString(),
        CSPPID: child?.CSPPID ?? '',
        FamilyIdentificationCaseNumber: faker.number.int(1000000).toString(),
        FamilySize: faker.number.int(6),
        FamilyIncome: faker.number.int({min: 5000, max: 200000, multipleOf: 1000}),
        CALWorksRecipient: core.calWORKsList[faker.number.int(core.calWORKsList.length - 1)].Value,
        HeadOfHousehold1ReasonsForNeedingService1: [core.reasonForServiceList[faker.number.int(core.calWORKsList.length - 1)].Value],
        HeadOfHousehold1LastName1: firstName1,
        HeadOfHousehold1LastName2: '',
        HeadOfHousehold1FirstName: lastName1,
        HeadOfHousehold1MiddleInitial: '',
        HeadOfHousehold1HighestEducationLevel: core.educationList[1 + faker.number.int(core.educationList.length - 2)].Value,
        HeadOfHousehold2ReasonsForNeedingService1: gender2 === undefined ? [] : [core.reasonForServiceList[faker.number.int(core.calWORKsList.length - 1)].Value],
        HeadOfHousehold2LastName1: gender2 === undefined ? undefined : faker.person.lastName(gender2),
        HeadOfHousehold2LastName2: '',
        HeadOfHousehold2FirstName: gender2 === undefined ? undefined : faker.person.firstName(gender2),
        HeadOfHousehold2MiddleInitial: gender2 === undefined ? undefined : "",
        HeadOfHousehold2HighestEducationLevel: gender2 === undefined ? undefined : core.educationList[1 + faker.number.int(core.educationList.length - 2)].Value,
        HeadOfHouseholdWrittenCommunicationPreference: core.languagesList[1 + faker.number.int(core.languagesList.length - 2)].Value,
        HeadOfHouseholdVerbalCommunicationPreference: core.languagesList[1 + faker.number.int(core.languagesList.length - 2)].Value,
        ResidentialAddress1: faker.location.streetAddress(),
        ResidentialAddress2: faker.location.secondaryAddress(),
        ResidentialCity: faker.location.city().replace(/[()]/g, ""),
        ResidentialState: 'CA',
        ResidentialZip: faker.location.zipCode('9####'),
        MailingAddress1: faker.location.streetAddress(),
        MailingAddress2: faker.location.secondaryAddress(),
        MailingCity: faker.location.city().replace(/[()]/g, ""),
        MailingState: 'CA',
        MailingZip: faker.location.zipCode('9####'),
        CurrentlyUnhoused: ['Y', 'N'][faker.number.int(1)],
        Email: `${firstName1}_${lastName1}@capsdac.org`,

        EffectiveStartDate: dateToLocalString(faker.date.between({ from: py.EffectiveStartDate, to: py.EffectiveEndDate })),
        EffectiveEndDate: null,

        Enrollments: [],
    }
}

export function newFamilies(core: CoreDataModel, data: LeaTestDataModel, numRecords: number, percentNew: number = 0): FAMI[] {
    if (data.fami.length == 0) percentNew = 100
    const familyPool = Array.from(Array(numRecords).keys(), (idx: number) => {
        return (faker.number.int(99) < percentNew) ? undefined : data.fami[faker.number.int(data.fami.length - 1)]
    })
    return Array.from(Array(numRecords).keys(), (idx: number) => {
        return newFamilyModel(core, data, familyPool[idx]);
    })
}

const newChildModel = (core: CoreDataModel, data: LeaTestDataModel, child: CHLD | undefined): CHLD => {
    const py = fakerProgramYear(core)
    const gender = core.genderList[faker.number.int(core.genderList.length - 1)].Value
    const country = ['US', 'MX', 'CA', core.countryList[faker.number.int(core.countryList.length - 1)].Value][faker.number.int(3)]
    const stateOptions = core.states.filter(x => x.CountryCode == country)
    const state = stateOptions.length === 0 ? "" : stateOptions[faker.number.int(stateOptions.length - 1)].Code
    const ethnicity = ['Y', 'N', ''][faker.number.int(2)]
    const race = core.raceList[faker.number.int(core.raceList.length)]?.Value || ''
    const dllValue = ['Y', 'N'][faker.number.int(1)]
    const localChildId = child?.CSPPID ?? faker.number.int(1000000).toString()
    const effectiveStart = faker.date.between({ from: py.EffectiveStartDate, to: py.EffectiveEndDate })
    return {
        Id: faker.number.int(1000000).toString(),
        ReportingLEA: data.lea.CDSCode,
        PreschoolCode: fakerArrayElement(data.preschools).PreschoolCDSCode,
        ProgramYear: py.Code,
        LocalChildId: localChildId,
        CSPPID: '',
        ChildLastName: faker.person.lastName(gender),
        ChildFirstName: faker.person.lastName(gender),
        ChildMiddleName: '',
        ChildGender: gender,
        ChildDateOfBirth: dateToLocalString(faker.date.birthdate({ min: 3, max: 5, mode: 'age' })),
        ChildCityOfBirth: faker.location.city().replace(/[()]/g, ""),
        ChildStateOfBirth: state,
        ChildCountryOfBirth: country,
        ChildEthnicity: ethnicity,
        ChildEthnicityMissingIndicator: ethnicity === '' ? 'Y' : 'N',
        ChildRace1: race,
        ChildRaceMissingIndicator: race === '' ? 'Y' : 'N',
        //FamilyLanguageInstrumentOptOut: ['Y', 'N'][faker.number.int(1)],
        //FamilyLanguageInstrumentDate: dateToYYYYMMDD(faker.date.past()),
        //LanguagesIdentifiedInFamilyInstrument: [1 + core.languagesList[faker.number.int(core.raceList.length - 2)].Value],
        //LanguagesHome: (dllValue === 'N') ? ['eng'] : [core.languagesList[1 + faker.number.int(core.languagesList.length - 2)].Value],
        //LanguageMostUsed: (dllValue === 'N') ? 'eng' : core.languagesList[1 + faker.number.int(core.languagesList.length - 2)].Value,
        //IsDualLanguageLearnerYN: dllValue,
        //IsDLLTeacherDesignatedYN: (dllValue === 'N') ? '' : ['Y', 'N'][faker.number.int(1)],
        EffectiveStartDate: dateToLocalString(effectiveStart),
        EffectiveEndDate: "",
        Enrollments: [],
        ClassroomAssignments: [],
        Attendance: [],
    }
}

export function newChildren(core: CoreDataModel, data: LeaTestDataModel, numRecords: number, percentNew: number = 0): CHLD[] {
    if (data.chld.length == 0) percentNew = 100
    const childPool = Array.from(Array(numRecords).keys(), (idx: number) => {
        return (faker.number.int(99) < percentNew) ? undefined : data.chld[faker.number.int(data.chld.length - 1)]
    })
    const records = Array.from(Array(numRecords).keys(), (idx: number) => {
        var c = newChildModel(core, data, childPool[idx]);
        return c;
    })
    const groupedCHLD = makeRecord<CHLD>(records, CHLD_Key, (x, y) => y.EffectiveStartDate.localeCompare(x.EffectiveStartDate))
    for (const [k, v] of Object.entries(groupedCHLD)) {
        for (var i: number = 0; i < v.length - 1; i++) {
            // Skip grouping for blank CSPPID
            if (v[i].CSPPID == '') break;
            // Prevent child records with same effective date
            if (v[i].EffectiveStartDate === v[i + 1].EffectiveStartDate) {
                v[i + 1].EffectiveStartDate = new Date(new Date(v[i].EffectiveStartDate).getTime() - 24 * 3600 * 1000).toISOString().slice(0, 10)
            }
        }
    }
    return records
}

export function newEnrollment(core: CoreDataModel, data: LeaTestDataModel, child: CHLD, id: string): ENRL {
    const py = fakerProgramYear(core)
    return {
        Id: id,
        ReportingLEA: data.lea.CDSCode,
        PreschoolCode: data.preschools[faker.number.int(data.preschools.length - 1)].PreschoolCDSCode,
        //PreschoolCode: child.PreschoolCode,
        ProgramYear: py.Code,
        CSPPID: child.CSPPID,
        LocalChildId: child.LocalChildId,
        ContractType: fakerArrayElement(core.contractTypesOfferedList).Value,
        ProgramType: fakerArrayElement(core.programTypeList).Value,
        ProgramDuration: fakerArrayElement(core.programDurationList).Value,
        //TimeBase: fakerArrayElement(core.timeBaseList).Value,
        EligibilityStatus: fakerArrayElement(core.eligibilityStatusList).Value,
        EnrollmentStartDate: dateToLocalString(faker.date.between({ from: py.EffectiveStartDate, to: py.EffectiveEndDate })),
        EnrollmentEndDate: "",
        EnrollmentEndReason: '',
    }
}

export function newEnrollments(core: CoreDataModel, data: LeaTestDataModel, extra_count: number): ENRL[] {
    const total_count = data.chld.length + extra_count;
    const idPool = faker.helpers.uniqueArray(() => faker.number.int(1000000).toString(), total_count);
    const childPool = Array.from(Array(total_count).keys(), (idx: number) => {
        return idx < data.chld.length ? data.chld[idx] : data.chld[faker.number.int(data.chld.length - 1)];
    });
    return Array.from(Array(total_count).keys(), (idx: number) => {
        var e = newEnrollment(core, data, childPool[idx], idPool[idx])
        return e;
    });
}


export function newStaffClassroom(core: CoreDataModel, data: LeaTestDataModel, staff: STFF, id: string): SCLR {
    const classes = data.clrm.filter(x => x.PreschoolCode === staff.PreschoolCode)
    return {
        Id: id,
        ReportingLEA: data.lea.CDSCode,
        PreschoolCode: staff.PreschoolCode,
        ProgramYear: staff.ProgramYear,
        LocalStaffId: staff.LocalStaffId,
        LocalClassroomId: (classes.length == 0) ? "idForMissingClassroom" : classes[faker.number.int(classes.length - 1)].LocalClassroomId,
        StaffClassroomRole: core.staffRoleList[faker.number.int(core.staffRoleList.length - 1)].Value,
    }
}

export function newStaffClassrooms(core: CoreDataModel, data: LeaTestDataModel, count: number): SCLR[] {
    const idPool = faker.helpers.uniqueArray(() => faker.number.int(1000000).toString(), count);
    const staffPool = Array.from(Array(count).keys(), (idx: number) => {
        return data.stff[faker.number.int(data.stff.length - 1)];
    });
    return Array.from(Array(count).keys(), (idx: number) => { return newStaffClassroom(core, data, staffPool[idx], idPool[idx]) });
}

export function newChildClassroom(core: CoreDataModel, data: LeaTestDataModel, enrollment: ENRL, id: string): CLEN {
    const child = data.chld.filter(x => x.CSPPID === enrollment.CSPPID)[0]
    const classes = data.clrm.filter(x => x.PreschoolCode === child.PreschoolCode)
    return {
        Id: id,
        ReportingLEA: data.lea.CDSCode,
        PreschoolCode: enrollment.PreschoolCode,
        ProgramYear: enrollment.ProgramYear,
        CSPPID: enrollment.CSPPID,
        LocalChildId: enrollment.LocalChildId,
        LocalClassroomId: (classes.length == 0) ? "idForMissingClassroom" : classes[faker.number.int(classes.length - 1)].LocalClassroomId,
    }
}

export function newChildClassrooms(core: CoreDataModel, data: LeaTestDataModel, extra_count: number): CLEN[] {
    const total_count = data.enrl.length + extra_count;
    const idPool = faker.helpers.uniqueArray(() => faker.number.int(1000000).toString(), total_count);
    const enrollmentPool = Array.from(Array(total_count).keys(), (idx: number) => {
        return idx < data.enrl.length ? data.enrl[idx] : data.enrl[faker.number.int(data.enrl.length - 1)];
    });
    return Array.from(Array(total_count).keys(), (idx: number) => {
        var cc = newChildClassroom(core, data, enrollmentPool[idx], idPool[idx])
        return cc;
    });
}

export function newAttendance(core: CoreDataModel, data: LeaTestDataModel, childClassroom: CLEN, id: string): ATTN {
    const daysAttended = faker.number.int({ min: 20, max: 30 });
    const daysAbsentExcused = faker.number.int(5)
    const daysAbsentUnexcused = faker.number.int(3)
    const daysSuspended = faker.number.int(3)
    return {
        Id: id,
        ReportingLEA: data.lea.CDSCode,
        PreschoolCode: childClassroom.PreschoolCode,
        ProgramYear: childClassroom.ProgramYear,
        CSPPID: childClassroom.CSPPID,
        LocalChildId: childClassroom.LocalChildId,
        LocalClassroomId: childClassroom.LocalClassroomId,
        FirstOfMonth: dateToLocalString(faker.date.past()),
        DaysExpected: daysAttended + daysAbsentExcused + daysAbsentUnexcused + daysAbsentUnexcused + daysSuspended,
        DaysAttended: daysAttended,
        DaysAbsentExcused: daysAbsentExcused,
        DaysAbsentUnexcused: daysAbsentUnexcused,
        DaysSuspended: daysSuspended,
    }
}

export function newAttendances(core: CoreDataModel, data: LeaTestDataModel, extra_count: number): ATTN[] {
    const total_count = data.clen.length + extra_count;
    const idPool = faker.helpers.uniqueArray(() => faker.number.int(1000000).toString(), total_count);
    const childClassroomPool = Array.from(Array(total_count).keys(), (idx: number) => {
        return idx < data.clen.length ? data.clen[idx] : data.clen[faker.number.int(data.clen.length - 1)];
    });
    const records = Array.from(Array(total_count).keys(), (idx: number) => { return newAttendance(core, data, childClassroomPool[idx], idPool[idx]) });
    const groupedATTN = makeRecord<ATTN>(records, (r) => `${r.CSPPID},${r.LocalClassroomId}`, (x, y) => y.FirstOfMonth.localeCompare(x.FirstOfMonth))
    for (const [k, v] of Object.entries(groupedATTN)) {
        for (var i: number = 0; i < v.length - 1; i++) {
            // Prevent attn records with same FirstOfMonth date. This is a hack since we're adding a day, instead of a month.
            if (v[i].FirstOfMonth === v[i + 1].FirstOfMonth) {
                v[i + 1].FirstOfMonth = new Date(new Date(v[i].FirstOfMonth).getTime() - 24 * 3600 * 1000).toISOString().slice(0, 10)
            }
        }
    }
    return records
}
